import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Alert,
  Box,
  Typography,
  Chip,
  Stack,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addPlayerToTournament, fetchEligibleUsers, Tournament } from '../../services/api';
import { GENDER_LABELS } from '../../constants/userConstants';

interface AddPlayerDialogProps {
  open: boolean;
  onClose: () => void;
  tournament: Tournament;
}

const AddPlayerDialog = ({ open, onClose, tournament }: AddPlayerDialogProps) => {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: usersData, isLoading, error: fetchError } = useQuery({
    queryKey: ['tournament', tournament.id, 'eligible-users'],
    queryFn: () => fetchEligibleUsers(tournament.id),
    enabled: open,
    retry: false,
  });

  // Ensure users is always an array
  const users = Array.isArray(usersData) ? usersData : [];

  const addPlayerMutation = useMutation({
    mutationFn: (playerId: string) => addPlayerToTournament(tournament.id, playerId),
    onSuccess: () => {
      // Invalidate tournament query to refresh the page
      queryClient.invalidateQueries({ queryKey: ['tournament', tournament.id] });
      queryClient.invalidateQueries({ queryKey: ['tournament', tournament.id, 'eligible-users'] });
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to add player');
    },
  });

  // Filter out players who are already registered in the tournament
  // Ensure users is an array before filtering
  const eligibleUsers = Array.isArray(users) 
    ? users.filter((user) => !tournament.registered_player_ids?.includes(user.id))
    : [];

  const handleSubmit = () => {
    if (!selectedUserId) {
      setError('Please select a player');
      return;
    }

    setError(null);
    // Close dialog immediately for better UX (optimistic UI)
    onClose();
    // Reset state
    setSelectedUserId('');
    setSuccess(null);
    // Submit in background
    addPlayerMutation.mutate(selectedUserId);
  };

  const handleClose = () => {
    setSelectedUserId('');
    setError(null);
    setSuccess(null);
    onClose();
  };

  const selectedUser = Array.isArray(users) ? users.find((u) => u && u.id === selectedUserId) : undefined;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Player to Tournament</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {!!fetchError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Unable to load eligible users.</strong>
            </Typography>
            <Typography variant="body2">
              There was an error fetching the list of eligible players. Please try again or contact support if the problem persists.
            </Typography>
          </Alert>
        )}

        {/* Show tournament restrictions */}
        {!fetchError && (tournament.allowed_genders || tournament.min_level || tournament.max_level) && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Tournament Eligibility Requirements:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {tournament.allowed_genders && tournament.allowed_genders.length > 0 && (
                tournament.allowed_genders.map((gender) => (
                  <Chip
                    key={gender}
                    label={GENDER_LABELS[gender]}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))
              )}
              {(tournament.min_level || tournament.max_level) && (
                <Chip
                  label={`Level: ${tournament.min_level || 'Any'} - ${tournament.max_level || 'Any'}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Stack>
          </Box>
        )}

        {!fetchError && <TextField
          fullWidth
          select
          label="Select Player"
          value={selectedUserId}
          onChange={(e) => setSelectedUserId(e.target.value)}
          disabled={isLoading || addPlayerMutation.isPending}
          sx={{ mt: 2 }}
        >
          <MenuItem value="">
            <em>Select a player...</em>
          </MenuItem>
          {eligibleUsers?.map((user) => (
            <MenuItem key={user.id} value={user.id}>
              {user.name} ({user.email})
              {user.gender && ` - ${GENDER_LABELS[user.gender as keyof typeof GENDER_LABELS]}`}
              {user.usta_level && ` - ${user.usta_level}`}
            </MenuItem>
          ))}
        </TextField>}

        {!fetchError && eligibleUsers?.length === 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            No eligible players available. All players matching the tournament's requirements have already been added.
          </Alert>
        )}

        {!fetchError && selectedUser && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Selected Player:
            </Typography>
            <Typography variant="body2">
              <strong>Name:</strong> {selectedUser.name}
            </Typography>
            <Typography variant="body2">
              <strong>Email:</strong> {selectedUser.email}
            </Typography>
            {selectedUser.gender && (
              <Typography variant="body2">
                <strong>Gender:</strong> {GENDER_LABELS[selectedUser.gender as keyof typeof GENDER_LABELS]}
              </Typography>
            )}
            {selectedUser.usta_level && (
              <Typography variant="body2">
                <strong>USTA Level:</strong> {selectedUser.usta_level}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={addPlayerMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedUserId || addPlayerMutation.isPending || isLoading || !!fetchError}
        >
          {addPlayerMutation.isPending ? 'Adding...' : 'Add Player'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPlayerDialog;
